import type { FlightCardData } from '@massilia/queries'

import { cn } from '@massilia/ui'
import { useMemo, useState } from 'react'

import { useDebug } from '@/components/flights/debug-context'

import { FlightCardChips } from './flight-card-chips'
import { FlightCardPrice } from './flight-card-price'
import { FlightInfoSection } from './flight-info-section'
import { JourneyOptionsPanel } from './journey-options-panel'

interface FlightCardProps {
  className?: string
  flight: FlightCardData
  onSelect?: (flight: FlightCardData, selectedJourneyKeys: string[]) => void
  transactionKey: string
}

export function FlightCard({ className, flight, onSelect, transactionKey }: FlightCardProps) {
  const isDebug = useDebug()

  // State for expansion and journey selection
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedJourneyKeys, setSelectedJourneyKeys] = useState<string[]>(
    flight.defaultSelectedJourneyKeys
  )

  // Calculate total number of journey options
  const totalOptionsCount = useMemo(() => {
    return flight.availableJourneys.reduce((acc, segment) => acc + segment.options.length, 0)
  }, [flight.availableJourneys])

  // Check if any segment has more than one option (i.e. has alternatives to choose from)
  const hasAlternativeOptions = useMemo(() => {
    return flight.availableJourneys.some((segment) => segment.options.length > 1)
  }, [flight.availableJourneys])

  // Get the current displayed flight data based on selected journeys
  const displayedFlight = useMemo(() => {
    const updatedSegments = selectedJourneyKeys.map((key) => flight.journeySegments[key])

    // Fallback if no segments resolved at all
    if (updatedSegments.every((s) => s === undefined)) {
      return flight
    }

    // For one-way or round-trip, update outbound/return
    if (flight.segments === undefined || flight.segments.length === 0) {
      return {
        ...flight,
        outbound: updatedSegments[0] ?? flight.outbound,
        return: updatedSegments[1] ?? flight.return,
      }
    }

    // For multi-destination, update segments array
    return {
      ...flight,
      segments: updatedSegments,
    }
  }, [flight, selectedJourneyKeys])

  // Handle journey selection change
  const handleSelectionChange = (segmentIndex: number, journeyKey: string) => {
    setSelectedJourneyKeys((prev) => {
      const newKeys = [...prev]
      newKeys[segmentIndex - 1] = journeyKey
      return newKeys
    })
  }

  return (
    <div
      data-flight-card
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md',
        className
      )}
    >
      {/*
        En disposition empilée, `contents` fait remonter l'en-tête, les segments
        et les pastilles au rang d'enfants directs de la carte : la barre de prix
        peut alors s'insérer entre les segments et les pastilles, et celles-ci
        terminent la carte (`order-last`). À partir de `lg`, le conteneur redevient
        la colonne de gauche et l'ordre du DOM reprend ses droits.

        Le passage à deux colonnes attend `lg` et non `md` : la colonne de prix
        fait 17rem fixes, et à 768px la carte n'en mesure que 438 — il ne restait
        que 164px à la section vol, où le bloc horaires débordait.
      */}
      <div className="flex flex-col lg:flex-row">
        <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-2 lg:p-4">
          {/*
            En-tête sur une seule ligne. Le logo faisait 60px et imposait à lui
            seul la hauteur de la ligne pour identifier une marque que le texte
            à côté nomme déjà ; « Opéré par X » est devenu un suffixe plutôt
            qu'une seconde ligne.
          */}
          <div className="flex min-w-0 items-center gap-2 px-3 pt-2.5 lg:p-0">
            {displayedFlight.validatingLogoUrl && (
              <img
                alt={`${displayedFlight.validatingAirline} logo`}
                className="size-7 shrink-0 object-contain lg:size-9"
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none'
                }}
                src={displayedFlight.validatingLogoUrl}
              />
            )}
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
              <span className="text-sm font-semibold text-gray-900 lg:text-base">
                {displayedFlight.validatingAirline}
              </span>
              {displayedFlight.validatingAirlineCode !== displayedFlight.airlineCode && (
                <span className="truncate text-[11px] text-gray-500 lg:text-xs">
                  opéré par {displayedFlight.airline}
                </span>
              )}
            </div>
            {isDebug &&
              (flight.allotmentStatus?.outboundIsAllotment ||
                flight.allotmentStatus?.returnIsAllotment) && (
                <span className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                  Allotement
                  {flight.allotmentStatus.outboundIsAllotment &&
                  flight.allotmentStatus.returnIsAllotment
                    ? ' (A+R)'
                    : flight.allotmentStatus.outboundIsAllotment
                      ? ' (aller)'
                      : ' (retour)'}
                </span>
              )}
          </div>

          {/* Flight info section */}
          <div className="flex flex-col gap-2 px-3 pt-2 lg:p-0">
            <FlightInfoSection flight={displayedFlight} />
          </div>

          {/* Pied de carte : bagages, financement (desktop) et horaires alternatifs. */}
          <div className="order-last px-3 pb-2.5 lg:order-none lg:p-0">
            <FlightCardChips
              flight={displayedFlight}
              hasAlternativeOptions={hasAlternativeOptions}
              isExpanded={isExpanded}
              onToggleExpand={() => {
                setIsExpanded((prev) => !prev)
              }}
              totalOptionsCount={totalOptionsCount}
            />
          </div>
        </div>

        {/* Right section - Pricing */}
        <div className="lg:shrink-0">
          <FlightCardPrice
            flight={flight}
            onSelect={() => {
              onSelect?.(flight, selectedJourneyKeys)
            }}
            pricing={flight.pricing}
            selectedJourneyKeys={selectedJourneyKeys}
            transactionKey={transactionKey}
          />
        </div>
      </div>

      {/* Expandable journey options panel */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded ? 'max-h-100 overflow-y-auto opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <JourneyOptionsPanel
          availableJourneys={flight.availableJourneys}
          onSelectionChange={handleSelectionChange}
          selectedJourneyKeys={selectedJourneyKeys}
        />
      </div>
    </div>
  )
}
